import { useCallback, useEffect } from "react";
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from "@chakra-ui/react";

import { Button } from "newui/components/Button";
import type { ScheduleColumn, ScheduleColumnType } from "../schedule-of-rates.types";
import { useScheduleEditor } from "../useScheduleEditor";
import { ScheduleColumnConfigPanel } from "./ScheduleColumnConfigPanel";
import { SchedulePreviewTable } from "./ScheduleOfRatesPreviewTable";

export interface EditScheduleOfRatesModalProps {
	initialColumns: ScheduleColumn[];
	isOpen: boolean;
	isSaving?: boolean;
	onClose: () => void;
	onSave: (columns: ScheduleColumn[]) => void;
	title?: string;
}

const EditScheduleOfRatesModal: React.FC<EditScheduleOfRatesModalProps> = ({
	initialColumns,
	isOpen,
	isSaving = false,
	onClose,
	onSave,
	title = "Edit Schedule Columns",
}) => {
	const {
		addColumn: editorAddColumn,
		columns: editorColumns,
		getColumns,
		moveColumn,
		removeColumn,
		reorderColumns,
		reset,
		updateColumnLabel,
		updateColumnType,
	} = useScheduleEditor({
		initialColumns,
		initialRows: [],
	});

	// Reset draft state when modal opens with new data
	useEffect(() => {
		if (isOpen) {
			reset(initialColumns, []);
		}
	}, [isOpen, initialColumns, reset]);

	const handleSave = useCallback(() => {
		const columns = getColumns();
		if (columns.length === 0) {
			return;
		}
		onSave(columns);
	}, [getColumns, onSave]);

	const handleAddColumn = useCallback(
		(type: ScheduleColumnType) => {
			editorAddColumn(type);
		},
		[editorAddColumn]
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="full"
			scrollBehavior="inside"
		>
			<ModalOverlay />
			<ModalContent data-testid="edit-schedule-modal">
				<ModalHeader>{title}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<div className="flex gap-6">
						{/* Left side: Column configuration */}
						<div className="w-1/2">
							<ScheduleColumnConfigPanel
								columns={editorColumns}
								onAddColumn={handleAddColumn}
								onLabelChange={updateColumnLabel}
								onMoveColumn={moveColumn}
								onRemoveColumn={removeColumn}
								onReorderColumns={reorderColumns}
								onTypeChange={updateColumnType}
							/>
						</div>

						{/* Right side: Preview */}
						<div className="w-1/2">
							<p className="mb-2 font-roboto text-sm font-semibold text-gray-900">
								Preview
							</p>
							<SchedulePreviewTable
								columns={editorColumns}
							/>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						className="mr-3"
						data-testid="cancel-edit-schedule"
						onClick={onClose}
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						data-testid="save-edit-schedule"
						disabled={isSaving || editorColumns.length === 0}
						onClick={handleSave}
					>
						{isSaving ? "Saving..." : "Save"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { EditScheduleOfRatesModal as EditScheduleModal };
